import { type ReactNode, useState } from "react";

import type { CitationRef } from "@/types";

// COORDINATE SYSTEM CONTRACT (post-iter-13.2 empirical correction) —
// MinerU adapter reads `content_list_v2.json` which emits bbox =
// [x0, y0, x1, y1] in 0-1000 NORMALIZED INTEGER SPACE (per
// pipeline_middle_json_mkcontent.py:482-488 `_build_bbox` source:
// `int(x * 1000 / page_width)`). Top-left origin, Y-axis DOWN, NOT
// PDF points and NOT image pixels.
//
// Empirical evidence (iter-13.2 PG probe of chunk c-237227...):
// bbox = [114.0, 282.0, 924.0, 316.0] — x1=924 > 595 (A4 width in
// PDF points) makes PDF-point interpretation impossible; 924 < 1000
// confirms 0-1000 normalized space.
//
// Math: percentage = bbox_value / 10 (since 0-1000 → 0-100%). Image
// natural dims are NOT needed in the math because the bbox is already
// page-fraction; they remain as a render guard (overlay suppressed
// until image loads, defends against onLoad-not-fired-yet AND broken
// image). Y-axis: NO flip needed — content_list bboxes are top-left
// origin per MinerU draw_bbox.py L37-56 (the Y-flip in that file is
// exclusively for reportlab PDF canvas drawing).
//
// Backend chunker writes raw 0-1000 normalized values WITHOUT
// conversion (services/rag-api/app/ingest/chunkers/proposition.py:250
// stores `list(b.bbox)` as-is). Storing the original wire format
// preserves MinerU's native semantic + zero re-ingest cost if future
// MinerU versions change to a different normalization base — only
// MINERU_BBOX_NORMALIZED_BASE here updates.
//
// Iter-13.1 (commit ba598e73) attempted a 150/72 PDF-point-to-pixel
// scale based on librarian's middle.json evidence; live empirical
// surfaced that MinerU adapter reads content_list_v2.json, not
// middle.json — so the 13.1 scale was wrong. This iter-13.2 fix is
// the empirically-verified correct math.
const MINERU_BBOX_NORMALIZED_BASE = 1000;

type BBox = readonly [number, number, number, number];

function normalizeBbox(bbox: CitationRef["bbox"]): BBox | null {
  if (!bbox || bbox.length !== 4) return null;
  const [x0, y0, x1, y1] = bbox;
  if (!Number.isFinite(x0) || !Number.isFinite(y0) || !Number.isFinite(x1) || !Number.isFinite(y1)) {
    return null;
  }
  if (x1 <= x0 || y1 <= y0) return null;
  return [x0, y0, x1, y1];
}

interface BboxOverlayProps {
  bbox: CitationRef["bbox"];
  imageWidth: number | null;
  imageHeight: number | null;
}

export function BboxOverlay({ bbox, imageWidth, imageHeight }: BboxOverlayProps) {
  const normalized = normalizeBbox(bbox);
  if (!normalized || !imageWidth || !imageHeight || imageWidth <= 0 || imageHeight <= 0) {
    return null;
  }
  const [x0, y0, x1, y1] = normalized;
  const base = MINERU_BBOX_NORMALIZED_BASE;
  return (
    <div
      aria-hidden="true"
      data-testid="bbox-overlay"
      style={{
        position: "absolute",
        left: `${(x0 / base) * 100}%`,
        top: `${(y0 / base) * 100}%`,
        width: `${((x1 - x0) / base) * 100}%`,
        height: `${((y1 - y0) / base) * 100}%`,
        border: "2px solid var(--accent-primary)",
        background: "var(--accent-soft)",
        pointerEvents: "none",
        borderRadius: "var(--radius-sm)",
        boxSizing: "border-box",
      }}
    />
  );
}

interface BboxListOverlayProps {
  bboxList: number[][] | null | undefined;
  imageWidth: number | null;
  imageHeight: number | null;
}

// Option D — multi-bbox overlay for proposition chunks (ADR-088).
// Renders N <BboxOverlay> rectangles for the bbox_list field on
// proposition CitationRefs (text-heavy chunks that span multiple
// page blocks; backend stamps the union of block bboxes).
// Pass-through pattern: each bbox flows through BboxOverlay's
// existing normalizeBbox guard (length 4 + finite + ordered), so
// malformed entries return null and never render. This means the
// total rendered count is naturally <= bboxList.length without an
// explicit pre-filter — matches BboxOverlay's null-on-invalid
// contract for single-bbox consistency.
export function BboxListOverlay({ bboxList, imageWidth, imageHeight }: BboxListOverlayProps) {
  if (!bboxList || bboxList.length === 0 || !imageWidth || !imageHeight) return null;
  return (
    <>
      {bboxList.map((bbox) => (
        <BboxOverlay key={bbox.join(",")} bbox={bbox} imageWidth={imageWidth} imageHeight={imageHeight} />
      ))}
    </>
  );
}

interface PageImageWithBboxProps {
  src: string;
  alt: string;
  bbox: CitationRef["bbox"];
  // Option D — multi-bbox overlay for proposition chunks (ADR-088).
  // When non-empty, takes precedence over single `bbox` (single overlay
  // is suppressed entirely so we do not render BOTH highlight modes
  // simultaneously). Backend invariant: a citation carries EITHER a
  // single bbox (single-block citations) OR a bbox_list (proposition
  // chunks spanning N blocks), so this routing matches the wire shape.
  // Future maintainer: do NOT change to additive rendering — the visual
  // semantic is "highlight the chunk's constituent block(s)", a single
  // mode per citation, not two overlapping modes.
  bboxList?: number[][] | null | undefined;
  className?: string;
  loading?: "lazy" | "eager";
  // Rendered INSTEAD of the image when it fails to load. A signed URL can point
  // at a blob that no longer exists (2026-10: a storage lifecycle rule deleted
  // every page screenshot older than 30 days), and the browser's broken-image
  // icon reads as a frontend bug rather than "no preview for this source".
  fallback?: ReactNode;
}

export function PageImageWithBbox({
  src,
  alt,
  bbox,
  bboxList,
  className,
  loading = "lazy",
  fallback,
}: PageImageWithBboxProps) {
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  // Keyed on src so switching to another citation retries its own image.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  if (fallback !== undefined && failedSrc === src) return <>{fallback}</>;
  return (
    <div className="relative inline-block w-full">
      <img
        src={src}
        alt={alt}
        loading={loading}
        className={className}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth > 0 && img.naturalHeight > 0) {
            setDims({ w: img.naturalWidth, h: img.naturalHeight });
          }
        }}
        onError={() => setFailedSrc(src)}
      />
      {bboxList && bboxList.length > 0 ? (
        <BboxListOverlay bboxList={bboxList} imageWidth={dims?.w ?? null} imageHeight={dims?.h ?? null} />
      ) : (
        <BboxOverlay bbox={bbox} imageWidth={dims?.w ?? null} imageHeight={dims?.h ?? null} />
      )}
    </div>
  );
}
